import { useEffect, useMemo, useRef } from 'react';

import { resolveTransactableEntity, resolveTransactableSettlement } from '@src/domains/transactables';
import { useTransactable } from '@src/features/transactables';

import { trackCheckoutCompleted } from '../checkoutTracking';
import { useCheckoutEventProps } from './useCheckoutEventProps';

export const useCheckoutCompletedEvent = () => {
  const props = useCheckoutEventProps();
  const transactable = useTransactable();

  const settled = useMemo(() => resolveTransactableSettlement(transactable), [transactable]);
  const pendingId = settled ? undefined : resolveTransactableEntity(transactable)?.id;

  // Only a purchase this checkout watched settle is this checkout's. One that shows up already paid
  // — a leftover of an earlier purchase, or a late hydration — was paid somewhere else.
  const seenPendingRef = useRef(new Set<string>());
  const trackedRef = useRef(new Set<string>());

  useEffect(() => {
    if (pendingId) {
      seenPendingRef.current.add(pendingId);
    }
  }, [pendingId]);

  useEffect(() => {
    if (!settled || !seenPendingRef.current.has(settled.id) || trackedRef.current.has(settled.id)) {
      return;
    }

    trackedRef.current.add(settled.id);

    trackCheckoutCompleted(props, { method: settled.method, totalPriceCents: settled.totalPriceCents });
  }, [settled, props]);
};

export default useCheckoutCompletedEvent;
